import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../crm-theme';
import { CrmBoardService } from '../../services/crm-board.service';
import { CrmLeadService } from '../../services/crm-lead.service';
import { BoardSummaryResponse } from '../../services/responses/board.response';
import { LeadResponse } from '../../services/responses/lead.response';

interface Props {
    lead: LeadResponse | null;
    currentBoardId: string;
    summaries: BoardSummaryResponse[];
    onCancel: () => void;
    onMoved: (boardName: string) => void;
}

/// Leva o lead a uma coluna de outro quadro; entra no topo, pois no fim da lista ninguém o veria.
export const CrmMoveBoardModal = ({ lead, currentBoardId, summaries, onCancel, onMoved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const others = summaries.filter((board) => board.active && board.id !== currentBoardId);

    const [boardId, setBoardId] = useState<string | null>(null);
    const [columns, setColumns] = useState<{ id: string; name: string }[]>([]);
    const [columnId, setColumnId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!lead) return;

        setBoardId((others.find((board) => board.isDefault) ?? others[0])?.id ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lead]);

    useEffect(() => {
        if (!boardId) return;

        setColumns([]);
        setColumnId(null);

        new CrmBoardService()
            .getById(boardId)
            .then((res) => {
                const list = (res.data.data?.columns ?? []).map((column) => ({ id: column.id, name: column.name }));
                setColumns(list);
                setColumnId(list[0]?.id ?? null);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardId]);

    const confirm = async () => {
        if (!lead || !columnId) return;

        setSaving(true);

        try {
            await new CrmLeadService().move(lead.id, { targetColumnId: columnId, targetSortOrder: 0 });
            onMoved(others.find((board) => board.id === boardId)?.name ?? '');
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={Boolean(lead)} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('crmKanban.moverQuadroTitulo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {lead?.contactName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: 13, color: cc.textSecondary, mb: 0.5 }}>
                        {t('crmKanban.quadroDestino')}
                    </Typography>
                    <StandaloneAutoCompleteSelect
                        options={others.map((board) => ({ value: board.id, label: board.name }))}
                        value={boardId}
                        onChange={(value) => setBoardId(value as string)}
                    />
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 13, color: cc.textSecondary, mb: 0.5 }}>
                        {t('crmKanban.colunaDestino')}
                    </Typography>
                    <StandaloneAutoCompleteSelect
                        options={columns.map((column) => ({ value: column.id, label: column.name }))}
                        value={columnId}
                        onChange={(value) => setColumnId(value as string)}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onCancel}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={confirm}
                    disabled={!columnId || saving}
                >
                    {t('crmKanban.moverConfirmar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
